"use client";

import * as React from "react";
import { cn } from "../primitives/cn";

export type SettingsNavItem = {
  id: string;
  label: string;
  href: string;
  active?: boolean;
  section?: string;
};

/**
 * SettingsLayout — v3 two-column settings shell with grouped side nav.
 */
export function SettingsLayout({
  title = "Settings",
  subtitle,
  navItems,
  onNavigate,
  children,
}: {
  title?: string;
  subtitle?: string;
  navItems: SettingsNavItem[];
  onNavigate: (href: string) => void;
  children: React.ReactNode;
}) {
  const sections = React.useMemo(() => {
    const map = new Map<string, SettingsNavItem[]>();
    for (const item of navItems) {
      const s = item.section ?? "General";
      const list = map.get(s) ?? [];
      list.push(item);
      map.set(s, list);
    }
    return map;
  }, [navItems]);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
          {title}
        </h1>
        {subtitle ? (
          <p className="max-w-3xl text-sm text-muted-foreground">{subtitle}</p>
        ) : null}
      </header>

      <div className="grid gap-6 md:grid-cols-[200px_minmax(0,1fr)]">
        <aside className="md:sticky md:top-4 md:self-start">
          <nav
            className={cn(
              "flex gap-1 overflow-x-auto border-b border-border pb-2 md:block md:space-y-4 md:overflow-visible md:border-0 md:pb-0",
            )}
            aria-label="Settings"
          >
            {[...sections.entries()].map(([section, items]) => (
              <div key={section} className="shrink-0 md:shrink">
                <p className="mb-1 hidden px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground md:block">
                  {section}
                </p>
                <ul className="flex gap-1 md:block md:space-y-0.5">
                  {items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => onNavigate(item.href)}
                        className={cn(
                          "whitespace-nowrap rounded-md px-3 py-1.5 text-left text-sm transition-colors duration-micro",
                          "hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          item.active
                            ? "bg-primary-soft font-medium text-primary-soft-foreground"
                            : "text-muted-foreground",
                        )}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>
        <div className="min-w-0">{children}</div>
      </div>
    </div>
  );
}

/**
 * SettingsSection — v3 inner card for a settings sub-page.
 */
export function SettingsSection({
  title,
  description,
  actions,
  children,
  className,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "rounded-lg border border-border bg-card p-4 sm:p-6",
        className,
      )}
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h2 className="text-base font-semibold">{title}</h2>
          {description ? (
            <p className="text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>
        ) : null}
      </div>
      {children}
    </section>
  );
}
